import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { assert, configureAssert } from 'assert-ts';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { AUTH_TOKEN_NAME, OIDC_TOKEN_NAME, setItem, USER_TOKEN_NAME } from '#util/cookies.ts';
import { sentryScrubbingHooks } from '#util/sentry-scrub.ts';
import { trackError, trackUnrewordedValidationError, ValidationError } from './api/errors.ts';
import { appRoute } from './App.tsx';
import { ContextProviders } from './ContextProviders.tsx';

const environment = import.meta.env.MODE;

if (environment === 'production' || environment === 'staging') {
  const integrations = [
    Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      // Specify the application keys that you specified in the Sentry bundler plugin
      filterKeys: ['dn-webclient'],
      // Defines how to handle errors that contain third party stack frames.
      behaviour: 'apply-tag-if-contains-third-party-frames',
    }),
    Sentry.extraErrorDataIntegration(),
  ];

  Sentry.init({
    environment: __SENTRY_ENVIRONMENT__,
    // NOTE: The envelope URL is hardcoded in .definednet/functions/sentrytunnel.ts
    // to prevent SSRF. Update both if the DSN changes.
    dsn: 'https://167af0ae6cd24240b61dc0dc4a6598f3@o920269.ingest.sentry.io/5865647',
    tunnel: '/sentrytunnel',
    // Queue envelopes in IndexedDB when the send fails (offline, or WAN dead behind a live Wi-Fi
    // link) and retry with backoff; the default transport drops them, which loses the error page's
    // own report for exactly the sessions that reach it. The error page's Refresh is a full reload,
    // which discards the in-page retry timer, so also drain the queue on startup.
    transport: (options) =>
      Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport)({
        ...options,
        flushAtStartup: true,
        shouldStore(envelope) {
          // Only persist error events — transactions and sessions fill the 30-slot queue
          // without carrying the diagnostic data this transport exists to preserve.
          //
          // Deliberately not also giving up once `retryDelay` reaches its 1 hour cap. This
          // callback runs on the retry path too, after the envelope has been `shift()`ed out
          // of the store, and a `false` there makes `send()` throw instead of `unshift()`ing
          // it back, with no further flush scheduled. Backoff hits the cap after ten failures
          // (~85 minutes), so a long dead-WAN session — the case this transport exists for —
          // would drop the queued report and refuse new ones. The 30-slot cap and Sentry's own
          // backoff already bound the queue.
          return envelope[1].some(([header]) => header.type === 'event');
        },
      }),
    integrations,
    // Sampled at 25%; raise after watching span usage. Profiling intentionally omitted.
    // deploy-preview runs our e2e suite, so trace volume there is mostly bots — sample it
    // far lower to keep usage down while retaining a trickle of real PR-preview traces.
    tracesSampleRate: __SENTRY_ENVIRONMENT__ === 'deploy-preview' ? 0.02 : 0.25,
    // Attaches sentry-trace/baggage headers so the backend can continue the trace. The api
    // doesn't run Sentry yet (Kibana + Prometheus instead), so this is inert today — kept
    // ready for if/when backend tracing lands.
    tracePropagationTargets: ['https://admin.defined.net', 'https://admin.undefined.works'],
    ...sentryScrubbingHooks,
  });

  // Report failures of `assert.soft` to Sentry when in prod.
  configureAssert({
    warningReporter(_failureType, message, props) {
      trackError(new Error(message, { cause: props }));
    },
  });
}

// Prevent number inputs from changing due to scroll wheel
document.addEventListener('wheel', () => {
  if (document.activeElement instanceof HTMLInputElement && document.activeElement.type === 'number') {
    document.activeElement.blur();
  }
});

// After a deploy, the old hashed chunk filenames no longer exist on the CDN.
// Browsers that cached the previous index.html will request stale chunks and get
// the SPA fallback (index.html served as text/html), which fails strict MIME checks.
// Reload once to pick up the new HTML with correct chunk references.
window.addEventListener('vite:preloadError', (event) => {
  const reloaded = sessionStorage.getItem('vite:preloadReload');
  if (reloaded) return;
  event.preventDefault();
  sessionStorage.setItem('vite:preloadReload', '1');
  window.location.reload();
});

if (environment !== 'test') {
  const container = assert(document.getElementById('root'));
  const root = createRoot(container);

  const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);
  const router = sentryCreateBrowserRouter(createRoutesFromChildren(appRoute));

  function handleFetchError(error: Error) {
    if (error instanceof ValidationError && !error.reported) {
      // Only track validation messages that are unexpected and haven't already been reported
      trackUnrewordedValidationError(error, trackError);
    } else {
      trackError(error);
    }
    // If error is not returned, it will seemingly be trapped here
    return error;
  }
  const queryCache = new QueryCache({ onError: handleFetchError });
  const mutationCache = new MutationCache({ onError: handleFetchError });

  const loadMSWMocks = () =>
    import('#mock/worker.browser.ts')
      .then(async ({ startWorker, mswWorker }) => {
        await startWorker();
        // Add a random "realistic" delay to all requests
        const { makeDelayHandler } = await import('#mock/msw/delay.ts');
        mswWorker.use(makeDelayHandler());

        return;
      })
      // Set up @msw/data
      .then(() => import('#mock/msw/db.ts'))
      .then(async ({ seedDb, seedDbProvisioned }) => {
        if (__IS_MOCK_NEW__) {
          await seedDbProvisioned();
        } else {
          await seedDb();
        }
        return;
      })
      .catch((err: unknown) => {
        console.error('Could not start up mock server:', err);
      });

  if (__IS_MOCKED__) {
    // Create a mock server for our api, if needed
    await loadMSWMocks();

    // Set auth tokens, as needed
    setItem(OIDC_TOKEN_NAME, __IS_MOCK_SSO__ ? 'oidcUser-mocked' : '', undefined, false);
    setItem(USER_TOKEN_NAME, !__IS_MOCK_SSO__ ? 'sess-mocked' : '', undefined, false);
    setItem(AUTH_TOKEN_NAME, !__IS_MOCK_SSO__ ? 'auth-mocked' : '', undefined, false);
  }

  root.render(
    <StrictMode>
      <ContextProviders
        client={
          new QueryClient({
            queryCache,
            mutationCache,
            defaultOptions: {
              queries: { networkMode: __IS_MOCKED__ ? 'always' : 'online' },
            },
          })
        }
      >
        <RouterProvider router={router} />
      </ContextProviders>
    </StrictMode>
  );
}
